import { z } from 'zod'
import { zCoerceNumber, zId, zIdNullable, zIsoDate, zJsonBlob } from '../common'
import { zBindingGoal, zBindingMode } from '../enums'

const zGoogleId = z.union([z.string(), z.number()]).transform(String)
const zGoogleIdList = z.array(zGoogleId).nullish().default([])

// One market = one campaign. The binding carries the market identity (locale,
// language constant, geo targets, landing prefix) plus the autopilot knobs.
export const zCampaignBinding = z.object({
  id: zId,
  siteId: zId,
  adsAccountId: zId,
  campaignId: zGoogleId,
  campaignName: z.string().nullish().default(null),
  mode: zBindingMode,
  goal: zBindingGoal.nullish().default(null),
  budgetDailyCeilingMicros: z.number().nullish().default(null),
  allowRsaAuto: z.boolean().nullish().default(false),
  locale: z.string().nullish().default(null),
  languageConstantId: zGoogleId.nullish().default(null),
  geoTargetConstantIds: zGoogleIdList,
  landingPathPrefix: z.string().nullish().default(null),
  conversionActionIds: zGoogleIdList,
  biddingStrategyType: z.string().nullish().default(null),
  targetCpaMicros: z.number().nullish().default(null),
  // Same BigDecimal risk as healthScore if Rails ever stringifies it.
  targetRoas: zCoerceNumber.nullish().default(null),
  allowBiddingAuto: z.boolean().nullish().default(false),
  // Mise en veille: freezes every apply/audit path server-side. `mode` is kept
  // intact so resuming restores the prior autopilot behaviour.
  paused: z.boolean().nullish().default(false),
  pausedAt: zIsoDate.nullish().default(null),
})
export type CampaignBinding = z.infer<typeof zCampaignBinding>

// Last audit summary inside the overview. `grade` is an open string (A…F
// today); the GradeChip maps its first letter, unknown grades stay neutral.
export const zLastAudit = z.object({
  id: zId,
  grade: z.string().nullish().default(null),
  // Rails BigDecimal → JSON string ("100.0"); coerce to number.
  healthScore: zCoerceNumber.nullish().default(null),
  wastedSpendMicros: z.number().nullish().default(null),
  finishedAt: zIsoDate.nullish().default(null),
})
export type LastAudit = z.infer<typeof zLastAudit>

// GET /campaign_bindings/:id/overview — the binding's weather report.
export const zBindingOverview = z.object({
  campaignBindingId: zId,
  campaignId: zGoogleId.nullish().default(null),
  mode: zBindingMode,
  lastAudit: zLastAudit.nullish().default(null),
  openProposalsMagnitudeMicros: z.number().nullish().default(null),
  // Count of open proposals; tolerate an array payload (then use its length).
  openProposals: z
    .union([z.number(), z.array(z.unknown()).transform((a) => a.length)])
    .nullish()
    .default(null),
})
export type BindingOverview = z.infer<typeof zBindingOverview>

// POST /campaign_bindings/bootstrap → 201/422 bootstrapRun. Creates a PAUSED
// Search campaign for a market; the binding comes back in `suggest` mode.
export const zBootstrapRun = z.object({
  id: zId,
  status: z.string(),
  campaignBindingId: zIdNullable.optional().default(null),
  plan: z.unknown().optional(),
  createdResources: z.unknown().optional(),
  error: z.string().nullish().default(null),
})
export type BootstrapRun = z.infer<typeof zBootstrapRun>

// POST /campaign_bindings/:id/rsa_suggestions — grounded RSA copy. Slow
// endpoint (~10–15 s: embeddings + LLM). NEVER auto-published by the API.
export const zRsaSuggestion = z.object({
  headlines: z.array(z.string()),
  descriptions: z.array(z.string()),
  groundedUrls: z.array(z.string()).nullish().default([]),
})
export type RsaSuggestion = z.infer<typeof zRsaSuggestion>

// Audit trigger → 202 (enqueued). Body shape is server-owned; keep it loose.
export const zAuditEnqueued = zJsonBlob

// POST /campaign_bindings/:id/interactions — create (or reuse) a WEBPAGE
// conversion action, attach it to the binding, return the Google tag snippets
// the site must paste. Affiliate-first defaults: OUTBOUND_CLICK + ONE_PER_CLICK.
export const zInteraction = z.object({
  id: zGoogleId,
  resourceName: z.string().nullish().default(null),
  name: z.string(),
  category: z.string(),
  reused: z.boolean().nullish().default(false),
})
export type Interaction = z.infer<typeof zInteraction>

export const zInteractionSnippet = z.object({
  globalSiteTag: z.string().nullish().default(''),
  eventSnippet: z.string().nullish().default(''),
  pending: z.boolean().nullish().default(false),
})
export type InteractionSnippet = z.infer<typeof zInteractionSnippet>

export const zRecordInteractionResponse = z.object({
  interaction: zInteraction,
  snippet: zInteractionSnippet,
  attachedToBinding: z.boolean(),
  campaignBindingId: zId,
  warning: z.string().nullish().default(null),
})
export type RecordInteractionResponse = z.infer<typeof zRecordInteractionResponse>

export const INTERACTION_CATEGORIES = [
  'OUTBOUND_CLICK',
  'PURCHASE',
  'SUBMIT_LEAD_FORM',
  'PAGE_VIEW',
  'SIGNUP',
  'DEFAULT',
] as const
export type InteractionCategory = (typeof INTERACTION_CATEGORIES)[number]
